import type { Media, Product } from "@spree/sdk";

/** Pick a usable image URL from a Media record, preferring card-sized renditions. */
function mediaImageUrl(media: Media | undefined): string | null {
  if (!media || media.media_type !== "image") return null;
  return (
    media.small_url ||
    media.medium_url ||
    media.mini_url ||
    media.original_url ||
    null
  );
}

/**
 * Resolve the image URL for a <ProductCard>.
 *
 * Prefers the product's `thumbnail_url`, then `primary_media`, then the first
 * image in `media`. The `media` fallback covers products whose image is
 * attached to a variant rather than the product master — Spree leaves
 * `thumbnail_url` and `primary_media` null in that case, which otherwise shows
 * a placeholder on the listing even though the detail gallery renders fine.
 *
 * Returns null when the product genuinely has no image.
 */
export function getCardImageUrl(product: Product): string | null {
  if (product.thumbnail_url) return product.thumbnail_url;

  const fromPrimary = mediaImageUrl(product.primary_media);
  if (fromPrimary) return fromPrimary;

  const firstImage = product.media?.find((m) => m.media_type === "image");
  return mediaImageUrl(firstImage);
}
